import React, { useState, useEffect } from 'react';
import { Section, Category, TransactionType } from '../types';
import { apiService } from '../services/api';

interface SectionCategorySelectorProps {
  currentSection?: string;
  currentCategory?: string;
  transactionType: TransactionType;
  onSectionChange: (sectionName: string, sectionId?: string) => void;
  onCategoryChange: (categoryName: string, categoryId?: string) => void;
  disabled?: boolean;
  sections: Section[] | null;
  loadCategories: (sectionName: string) => Promise<Category[]>;
}

export const SectionCategorySelector: React.FC<SectionCategorySelectorProps> = ({
  currentSection,
  currentCategory,
  transactionType,
  onSectionChange,
  onCategoryChange,
  disabled = false,
  sections: sectionsData,
  loadCategories: loadCategoriesFromParent,
}) => {
  const [filteredSections, setFilteredSections] = useState<Section[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState<string>('');

  // Filter sections by transaction type when sectionsData changes
  useEffect(() => {
    console.log('SectionCategorySelector - sectionsData:', sectionsData);
    console.log('SectionCategorySelector - transactionType:', transactionType);
    
    if (sectionsData) {
      // Filter sections array by transaction type
      // Note: API uses 'EXPENSES' but frontend uses 'EXPENSE', so handle both
      const expectedType = transactionType === 'EXPENSE' ? 'EXPENSES' : transactionType;
      const filtered = sectionsData.filter(section => 
        section.transaction_type === expectedType || section.transaction_type === transactionType
      );
      console.log('SectionCategorySelector - filtered sections:', filtered);
      setFilteredSections(filtered || []);
    } else {
      setFilteredSections([]);
    }
  }, [sectionsData, transactionType]);

  // Load categories when section changes
  useEffect(() => {
    if (currentSection) {
      loadCategories(currentSection);
    } else {
      setCategories([]);
    }
  }, [currentSection]);

  const loadCategories = async (sectionName: string) => {
    try {
      setLoadingCategories(true);
      setError('');
      const categoriesData = await loadCategoriesFromParent(sectionName);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Failed to load categories');
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const sectionName = e.target.value;
    const selectedSection = filteredSections.find(s => s.section_name === sectionName);
    const sectionId = selectedSection?.create_date;
    onSectionChange(sectionName, sectionId);
    // Clear category when section changes
    onCategoryChange('');
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryName = e.target.value;
    const selectedCategory = categories.find(c => c.category_name === categoryName);
    const categoryId = selectedCategory?.create_date; // The create_date serves as the category ID
    onCategoryChange(categoryName, categoryId);
  };

  if (error) {
    return (
      <div className="text-red-600 text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Section Dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Section
        </label>
        <select
          value={currentSection || ''}
          onChange={handleSectionChange}
          disabled={disabled || !sectionsData}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">
            {!sectionsData ? 'Loading sections...' : 'Select a section'}
          </option>
          {filteredSections && filteredSections.map((section) => (
            <option key={section.section_name} value={section.section_name}>
              {section.section_name}
            </option>
          ))}
        </select>
      </div>

      {/* Category Dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <select
          value={currentCategory || ''}
          onChange={handleCategoryChange}
          disabled={disabled || !currentSection || loadingCategories}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">
            {!currentSection 
              ? 'Select a section first'
              : loadingCategories 
                ? 'Loading categories...' 
                : 'Select a category'
            }
          </option>
          {categories && categories.map((category) => (
            <option key={category.category_name} value={category.category_name}>
              {category.category_name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};